// HPCProfile/index.js - Main HPC Profile page for Secondary stage students (Grades 9-12)

import React, { useEffect, useState } from 'react';
import { Alert, message, Spin, Tabs } from 'antd';
import { getAccessToken, getRole } from '../../authClient';
import Layout from "../../Layout";
import PartA2 from './PartA2';
import PartA3 from './PartA3';
import PartA4 from './PartA4';
import PartA5 from './PartA5';
import PartA6 from './PartA6';
import { FileText, Target, Clock, Map, Award, BookOpen } from 'lucide-react';
import './HPCProfile.css';
import { API_CONFIG } from '../../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

const { TabPane } = Tabs;

const HPCProfile = () => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [eligible, setEligible] = useState(false);
    const [eligibilityMessage, setEligibilityMessage] = useState('');
    const [activeTab, setActiveTab] = useState('part-a2');
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 60);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        // Get user info from API
        const getUserInfo = async () => {
            try {
                const token = getAccessToken();
                if (token) {
                    const response = await fetch(`${API_BASE_URL}/user/stage`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (response.ok) {
                        const stageData = await response.json();
                        setUser({
                            role: getRole(),
                            grade: stageData.grade,
                            stage: stageData.stage
                        });
                    } else {
                        // Fallback
                        setUser({ role: getRole(), grade: '9' });
                    }
                }
            } catch (error) {
                console.error('Error getting user info:', error);
                // Fallback
                setUser({ role: getRole(), grade: '9' });
            }
        };

        getUserInfo();
        checkEligibility();
    }, []);

    const checkEligibility = async () => {
        try {
            const token = getAccessToken();

            // Check eligibility first
            const eligibilityResponse = await fetch(`${API_BASE_URL}/secondary/user/eligibility`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!eligibilityResponse.ok) {
                throw new Error('Failed to check eligibility');
            }

            const eligibilityData = await eligibilityResponse.json();
            setEligible(eligibilityData.eligible);
            setEligibilityMessage(eligibilityData.message);
        } catch (error) {
            console.error('Error checking eligibility:', error);
            message.error('Failed to check eligibility');
            setEligibilityMessage('Error checking eligibility');
        } finally {
            setLoading(false);
        }
    };

    // Tab Configuration
    const tabsConfig = [
        {
            key: 'part-a2',
            label: 'Self-Evaluation',
            subLabel: 'Part A (2)',
            icon: <Target className="w-5 h-5" />
        },
        {
            key: 'part-a3',
            label: 'Time Management',
            subLabel: 'Part A (3)',
            icon: <Clock className="w-5 h-5" />
        },
        {
            key: 'part-a4',
            label: 'Plans after School',
            subLabel: 'Part A (4)',
            icon: <Map className="w-5 h-5" />
        },
        {
            key: 'part-a5',
            label: 'Accomplishments',
            subLabel: 'Part A (5)',
            icon: <Award className="w-5 h-5" />
        },
        {
            key: 'part-a6',
            label: 'Courses & Skills',
            subLabel: 'Part A (6)',
            icon: <BookOpen className="w-5 h-5" />
        }
    ];

    const renderActiveComponent = () => {
        switch (activeTab) {
            case 'part-a2': return <PartA2 />;
            case 'part-a3': return <PartA3 />;
            case 'part-a4': return <PartA4 />;
            case 'part-a5': return <PartA5 />;
            case 'part-a6': return <PartA6 />;
            default: return <PartA2 />;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-50 to-orange-50 font-[poppins]">
                    <div className="text-center">
                        <div className="relative inline-block">
                            <div className="w-20 h-20 border-4 border-gray-200 border-t-[#D67C2C] rounded-full animate-spin"></div>
                        </div>
                        <p className="mt-6 text-gray-600 font-medium text-lg">Loading HPC Profile...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!eligible) {
        return (
            <Layout>
                <div className="min-h-screen bg-gradient-to-br from-gray-50 via-orange-50 to-red-50 font-[poppins] p-8">
                    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
                        <div className="p-12 text-center">
                            <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-6">
                                <FileText className="w-10 h-10 text-[#D67C2C]" />
                            </div>
                            <h1 className="text-4xl font-bold text-[#D67C2C] mb-4">
                                HPC Profile
                            </h1>
                            <h2 className="text-2xl font-semibold text-gray-800 mb-6">
                                Access Restricted
                            </h2>
                            <Alert
                                message={eligibilityMessage}
                                type="warning"
                                showIcon
                                className="text-left max-w-2xl mx-auto"
                            />
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-orange-50 font-[poppins]">

                {/* Sticky Header — shrinks on scroll */}
                <div
                    className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30 transition-all duration-300"
                    style={{ boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.08)' : undefined }}
                >
                    {/* Title row — hidden when scrolled */}
                    <div
                        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden transition-all duration-300"
                        style={{ maxHeight: scrolled ? 0 : 80, opacity: scrolled ? 0 : 1, paddingTop: scrolled ? 0 : undefined, paddingBottom: scrolled ? 0 : undefined }}
                    >
                        <div className="flex items-center gap-4 py-4">
                            <div className="bg-gradient-to-br from-[#D67C2C] to-[#F17C45] p-3 rounded-xl shadow-lg">
                                <FileText className="w-8 h-8 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">HPC Profile</h1>
                                <p className="text-sm text-gray-500 font-medium">
                                    Grade {user?.grade} • Holistic Progress Card
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Navigation Tabs */}
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className={`flex gap-1.5 overflow-x-auto scrollbar-hide transition-all duration-300 ${scrolled ? 'py-1.5' : 'pb-3 mt-1'}`}>
                            {tabsConfig.map((tab) => (
                                <button
                                    key={tab.key}
                                    onClick={() => setActiveTab(tab.key)}
                                    className={`
                                        relative group flex items-center gap-2 transition-all duration-300 border rounded-lg
                                        ${scrolled ? 'px-3 py-1.5' : 'px-3 py-2 min-w-[140px]'}
                                        ${activeTab === tab.key
                                            ? 'bg-gradient-to-br from-[#D67C2C] to-[#F17C45] border-transparent shadow-md' + (scrolled ? '' : ' transform -translate-y-0.5')
                                            : 'bg-white border-gray-200 hover:border-orange-300 hover:shadow-sm'
                                        }
                                    `}
                                >
                                    <div className={`
                                        rounded-md transition-colors flex-shrink-0 p-1
                                        ${activeTab === tab.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500 group-hover:bg-orange-50 group-hover:text-orange-500'}
                                    `}>
                                        <span className="w-4 h-4 block [&>svg]:w-4 [&>svg]:h-4">{tab.icon}</span>
                                    </div>
                                    <div className="text-left">
                                        {!scrolled && (
                                            <div className={`text-[10px] font-semibold leading-none mb-0.5 ${activeTab === tab.key ? 'text-white/80' : 'text-gray-400'}`}>
                                                {tab.subLabel}
                                            </div>
                                        )}
                                        <div className={`font-semibold whitespace-nowrap text-xs ${activeTab === tab.key ? 'text-white' : 'text-gray-700'}`}>
                                            {tab.label}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden min-h-[600px] border border-gray-100">
                        {renderActiveComponent()}
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default HPCProfile;